/**
 * GA4 helpers for the Audi Nuevo form.
 *
 * The current property keeps receiving exactly the same hits; the new property
 * is added in parallel only when its measurement id is configured.
 */

/**
 * @typedef {{ trackingId: string }} Ga4TrackingConfig
 */

/**
 * Builds the react-ga4 `initialize` configs for the Audi Nuevo form.
 * The current property always goes first because react-ga4 loads gtag.js with the first id.
 *
 * @param {string} currentMeasurementId - Measurement id already in production.
 * @param {string | null | undefined} newMeasurementId - Optional additional measurement id.
 * @returns {Ga4TrackingConfig[]}
 */
export const buildAudiNuevoGa4Configs = (currentMeasurementId, newMeasurementId) => {
  const configs = [{ trackingId: currentMeasurementId }];
  const trimmedNewMeasurementId = typeof newMeasurementId === "string" ? newMeasurementId.trim() : "";

  if (trimmedNewMeasurementId && trimmedNewMeasurementId !== currentMeasurementId) {
    configs.push({ trackingId: trimmedNewMeasurementId });
  }

  return configs;
};

/** GA4 drops event parameter values longer than 100 characters. */
const GA4_PARAM_VALUE_MAX_LENGTH = 100;
const DIRECT_SOURCE = "(direct)";
const NO_MEDIUM = "(none)";
const NOT_SET = "(not set)";
const NO_REFERRER = "(none)";

/**
 * @typedef {{
 *   utm_source?: string | null,
 *   utm_medium?: string | null,
 *   utm_campaign?: string | null,
 *   utm_term?: string | null,
 *   utm_content?: string | null,
 *   utm_id?: string | null,
 * }} UtmParams
 */

/**
 * @typedef {{
 *   lead_source: string,
 *   lead_medium: string,
 *   lead_campaign: string,
 *   lead_term: string,
 *   lead_content: string,
 *   lead_campaign_id: string,
 *   lead_referrer: string,
 * }} LeadAttributionParams
 */

/**
 * @param {unknown} value
 * @returns {string} Trimmed value cut to the GA4 limit, or an empty string when missing.
 */
const toGa4ParamValue = (value) =>
  typeof value === "string" ? value.trim().slice(0, GA4_PARAM_VALUE_MAX_LENGTH) : "";

/**
 * @param {string | null | undefined} referrer - Full referrer URL (e.g. `document.referrer`).
 * @returns {string} Referrer hostname, or `(none)` when empty or invalid.
 */
const toReferrerHostname = (referrer) => {
  try {
    return toGa4ParamValue(new URL(referrer).hostname) || NO_REFERRER;
  } catch (_invalidUrlError) {
    return NO_REFERRER;
  }
};

/**
 * Builds the campaign parameters of the lead event from the same UTM object sent to Salesforce.
 * Values are forwarded as received (no case or separator changes) so GA4 and Salesforce can be
 * reconciled 1:1; only missing values get a label and long values are truncated.
 * Parameters are prefixed `lead_` because `source`, `medium` and `campaign` are reserved by GA4.
 *
 * @param {UtmParams | null | undefined} utms
 * @param {string | null | undefined} [referrer]
 * @returns {LeadAttributionParams}
 */
export const buildLeadAttributionParams = (utms, referrer) => {
  const source = toGa4ParamValue(utms?.utm_source);
  const medium = toGa4ParamValue(utms?.utm_medium);
  const campaign = toGa4ParamValue(utms?.utm_campaign);
  const term = toGa4ParamValue(utms?.utm_term);
  const content = toGa4ParamValue(utms?.utm_content);
  const campaignId = toGa4ParamValue(utms?.utm_id);

  const hasAnyUtm = Boolean(source || medium || campaign || term || content || campaignId);

  return {
    lead_source: source || (hasAnyUtm ? NOT_SET : DIRECT_SOURCE),
    lead_medium: medium || (hasAnyUtm ? NOT_SET : NO_MEDIUM),
    lead_campaign: campaign || NOT_SET,
    lead_term: term || NOT_SET,
    lead_content: content || NOT_SET,
    lead_campaign_id: campaignId || NOT_SET,
    lead_referrer: toReferrerHostname(referrer),
  };
};

/**
 * Returns a copy of the event parameters routed to a single GA4 property.
 * `send_to` is a gtag routing directive, not a parameter stored by GA4.
 *
 * @param {Record<string, unknown>} eventParams
 * @param {string | null | undefined} measurementId
 * @returns {Record<string, unknown>}
 */
export const withSendTo = (eventParams, measurementId) => {
  if (!measurementId) {
    return eventParams;
  }

  return { ...eventParams, send_to: measurementId };
};

const GENERAL_FORM_MODEL = "general";
const AUDI_NUEVO_FORM_NAME = "audi_nuevo";
const LEAD_VALUE = 1;
const LEAD_CURRENCY = "MXN";

/**
 * @typedef {{
 *   utms?: UtmParams | null,
 *   referrer?: string | null,
 *   modelPathName?: string | null,
 *   formType?: string | null,
 *   transactionId?: string | null,
 * }} LeadContext
 */

/**
 * Builds the `generate_lead` parameters for the new GA4 property.
 * The model comes only from the URL, like the current property: the general form always
 * sends `general`, even when a model is chosen in the list.
 *
 * @param {LeadContext} leadContext
 * @returns {LeadAttributionParams & {
 *   vehicle_model: string, form_type: string, form_name: string,
 *   transaction_id: string, value: number, currency: string,
 * }}
 */
export const buildGenerateLeadParams = ({ utms, referrer, modelPathName, formType, transactionId }) => ({
  ...buildLeadAttributionParams(utms, referrer),
  vehicle_model: toGa4ParamValue(modelPathName) || GENERAL_FORM_MODEL,
  form_type: toGa4ParamValue(formType) || NOT_SET,
  form_name: AUDI_NUEVO_FORM_NAME,
  transaction_id: toGa4ParamValue(transactionId) || NOT_SET,
  value: LEAD_VALUE,
  currency: LEAD_CURRENCY,
});

/**
 * Sends `generate_lead` to the new GA4 property only.
 * Never throws: analytics must not affect the lead submission or the redirect.
 *
 * @param {{ isInitialized: boolean, event: (name: string, params: Record<string, unknown>) => void }} analytics - react-ga4 instance.
 * @param {string | null | undefined} newMeasurementId
 * @param {LeadContext} leadContext
 * @returns {void}
 */
export const sendGenerateLead = (analytics, newMeasurementId, leadContext) => {
  const trimmedNewMeasurementId = typeof newMeasurementId === "string" ? newMeasurementId.trim() : "";

  if (!trimmedNewMeasurementId || !analytics?.isInitialized) {
    return;
  }

  try {
    analytics.event("generate_lead", withSendTo(buildGenerateLeadParams(leadContext), trimmedNewMeasurementId));
  } catch (analyticsError) {
    console.error("GA4 generate_lead could not be sent", analyticsError);
  }
};
